import { AnimatePresence, motion } from 'framer-motion'
import {
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { InView } from 'react-intersection-observer'
import { doNothing } from 'remeda'
import { NotifyForm } from '@components/NotifyForm/NotifyForm'
import { NotifySimilarProducts } from '@components/NotifySimilarProducts/NotifySimilarProducts'
import { ProductStickyBar } from '@components/ProductStickyBar/ProductStickyBar'
import SizeSelector from '@components/SizeSelector/SizeSelector'
import { WishlistCTA } from '@components/WishlistCTA/WishlistCTA'
import { requestNotification } from '@helpers'
import { useCart } from '@velasca/hooks/use-cart'
import { useFooter } from '@velasca/hooks/use-footer'
import { useHeader } from '@velasca/hooks/use-header'
import type { Status } from '@velasca/types'
import { Button } from '@velasca/ui/button'
import { SansText } from '@velasca/ui/sans-text'
import { SansTitle } from '@velasca/ui/sans-title'
import { getBackInStockTranslationKey } from '@velasca/utils/get-back-in-stock-translation-key'
import { getSkuFromCurrentProduct } from '@velasca/utils/get-sku-from-current-product'
import { getStoreNetsuiteId } from '@velasca/utils/get-store-netsuite-id'
import { getSubsidiary } from '@velasca/utils/get-subsidiary'
import { $permanent_domain, $settings } from '@velasca/utils/globals'
import { handleError } from '@velasca/utils/handle-error'
import { isEmail } from '@velasca/utils/is-email'
import {
  AddToCart,
  AddToCartButtonWrapper,
  Form,
  SizeGuideButton,
  StyledErrorMessage,
  WrapperSizePicker,
  WrapText,
} from './SizePicker.styles'
import type { SizePickerProps } from './SizePicker.types'

const defaultNotifyMeEmail = localStorage.getItem('notifyMeEmail')

const stickyBarEl = document.getElementById('product-page-sticky-bar')

export const SizePicker = ({
  product,
  title,
  sizes,
  link,
  setIsModalOpen = doNothing,
  showButton,
  isGiftCard = false,
  notifyMe,
}: SizePickerProps) => {
  const { t: productTranslation } = useTranslation('product')
  const { t: cartDrawerTranslation } = useTranslation('cartDrawer')
  const { t: globalTranslation } = useTranslation('global')
  const { navbarHeight, promotionalBannerHeight } = useHeader()

  const [status, setStatus] = useState<Status>('idle')
  const [selectedSize, setSelectedSize] = useState<string | null>(null)
  const [hasUserSelectedVariant, setHasUserSelectedVariant] = useState(false)
  const [isBlinking, setIsBlinking] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const isFooterInView = useFooter((state) => state.isInView)

  const hasOneSize = sizes?.length === 1
  const defaultSize = hasOneSize ? sizes[0].id : null

  useEffect(() => {
    if (isBlinking && ref.current) {
      ref.current.onanimationend = () => {
        setIsBlinking(false)
      }
    }
  }, [isBlinking, ref, setIsBlinking])

  useEffect(() => {
    setSelectedSize(defaultSize)
  }, [defaultSize, setSelectedSize])

  // Reset the user-selected flag when the product changes
  useEffect(() => {
    setHasUserSelectedVariant(false)
  }, [product?.productId])

  const isSizeSelected = useMemo(() => {
    return selectedSize !== null
  }, [selectedSize])

  const currentSize = useMemo(
    () => sizes?.find((size) => size.id === selectedSize),
    [sizes, selectedSize],
  )

  // TODO: ripensare come gestire la taglia non disponibile quando il prodotto non è selezionato
  const isSizeAvailable = useMemo(() => {
    if (!isSizeSelected) {
      return false
    }

    if (!currentSize) {
      return false
    }

    return currentSize.isAvailable
  }, [isSizeSelected, currentSize])

  const buttonMessage = useMemo<string>(() => {
    // Adding to cart
    if (status === 'loading') {
      return productTranslation('addingToCart')
    }

    // Size is not selected
    if (!isSizeSelected) {
      if (isGiftCard) {
        return productTranslation('sizeTitleGiftCard')
      }

      return productTranslation('chooseSize')
    }

    // Size is selected and available
    if (isSizeSelected && isSizeAvailable) {
      return productTranslation('addToCart')
    }

    // Default
    return ''
  }, [isSizeSelected, isSizeAvailable, status, productTranslation, isGiftCard])

  const stickyBarButtonMessage = useMemo<string>(() => {
    // Adding to cart
    if (status === 'loading') {
      return productTranslation('addingToCart')
    }

    // Size is not available
    if (isSizeSelected && !isSizeAvailable) {
      return productTranslation('notifyMeButton')
    }

    // Size is selected and available
    if (!isSizeSelected || (isSizeAvailable && isSizeSelected)) {
      return productTranslation('addToCart')
    }

    // Default
    return ''
  }, [isSizeSelected, isSizeAvailable, status, productTranslation])

  const { addToCart, setIsDrawerOpen } = useCart()

  const [notifyMeLoading, setNotifyMeLoading] = useState(false)
  const [notifyMeComplete, setNotifyMeComplete] = useState(false)
  const [notifyMeEmail, setNotifyMeEmail] = useState(defaultNotifyMeEmail)
  const [notifyMeCanSubmit, setNotifyMeCanSubmit] = useState(false)

  const [error, setError] = useState<string | null>(null)

  const isDiscontinued = useMemo(() => {
    return !!notifyMe && notifyMe === 'limited'
  }, [notifyMe])

  useEffect(() => {
    setNotifyMeComplete(false)
  }, [selectedSize])

  const notifyMeSubmit = useCallback(() => {
    if (!notifyMeEmail) {
      return
    }

    $settings.klaviyo_enabled &&
      window.klaviyo?.identify({ $email: notifyMeEmail })

    const sku = getSkuFromCurrentProduct(
      selectedSize,
      window.meta.product.variants,
    )

    if (!sku) {
      return
    }

    setNotifyMeLoading(true)
    localStorage.setItem('notifyMeEmail', notifyMeEmail)

    const product_url = window.location.origin + window.location.pathname // Tolgo i parametri dall'url perché Netsuite non accetta più di 300 caratteri nell'url
    const language: string = $settings.ns_lang_value ?? '25'

    const subsidiary = getSubsidiary($settings.store_nation)

    if (!$permanent_domain) {
      handleError(new Error('$permanent_domain is not defined'))

      return
    }

    const store_netsuite_id = getStoreNetsuiteId($permanent_domain)

    if (!store_netsuite_id) {
      handleError(
        new Error('you passed a permanent_domain not available on Netsuite'),
      )

      return
    }

    requestNotification(
      sku,
      notifyMeEmail,
      product_url,
      language,
      subsidiary,
      store_netsuite_id,
    )
      .catch(handleError)
      .finally(() => {
        setNotifyMeComplete(true)
        setNotifyMeLoading(false)
      })
  }, [notifyMeEmail, selectedSize])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const ENTER_KEY = 'Enter'

      if (ENTER_KEY === event.key && notifyMeCanSubmit) {
        notifyMeSubmit()
      }
    },
    [notifyMeCanSubmit, notifyMeSubmit],
  )

  const handleOnSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()

      document.dispatchEvent(
        new CustomEvent('product:addToCart', {
          detail: { addId: selectedSize },
        }),
      )

      if (!selectedSize) {
        return
      }

      setStatus('loading')
      addToCart(selectedSize, 1)
        .then(() => {
          setStatus('idle')
          setIsDrawerOpen(true)
        })
        .catch((error) => {
          setStatus('error')

          if (error.response) {
            setError(error.response.data.description)
            handleError(error)
          } else {
            setError(cartDrawerTranslation('cart_drawer_product_error'))
            handleError(error, {
              message: cartDrawerTranslation('cart_drawer_product_error'),
            })
          }
        })
        .finally(() => {
          setTimeout(() => {
            if (!hasOneSize) {
              setSelectedSize(null)
            }
            setStatus('idle')
          }, 1000)
        })
    },
    [
      selectedSize,
      setStatus,
      addToCart,
      setIsDrawerOpen,
      cartDrawerTranslation,
      hasOneSize,
      setSelectedSize,
    ],
  )

  // Resets the loading state after 1 second, to prevent the addToCartButton to
  // be stuck in a loading state. It happens only on Safari and iOS devices because
  // of the back-forward cache
  useEffect(() => {
    setTimeout(() => {
      if (status !== 'idle') {
        setStatus('idle')
      }
    }, 1000)
  }, [setStatus, status])

  useEffect(() => {
    notifyMeEmail && setNotifyMeCanSubmit(isEmail(notifyMeEmail))
  }, [notifyMeEmail])

  const selectedVariantId = useMemo(() => {
    if (!selectedSize) {
      return null
    }
    const variant = sizes.find((size) => size.id === selectedSize)

    return variant ? variant.id : undefined
  }, [selectedSize, sizes])

  return (
    <WrapperSizePicker ref={ref} id="size-picker" isBlinking={isBlinking}>
      {!hasOneSize && (
        <WrapText>
          <SansTitle fontWeight="500" as="h3" size="xs">
            {title}
          </SansTitle>
          {showButton && link && (
            <SizeGuideButton
              onClick={() => {
                setIsModalOpen(true)
              }}
            >
              <SansText fontStyle="italic" color="dark">
                {link}
              </SansText>
            </SizeGuideButton>
          )}
        </WrapText>
      )}

      <Form id="addToCartForm" onSubmit={handleOnSubmit}>
        {hasOneSize ? (
          <input type="hidden" name="id" value={sizes[0].id} />
        ) : (
          <SizeSelector
            selectedSize={selectedSize}
            sizes={sizes}
            isGiftCard={isGiftCard}
            onSizeChange={(value: string) => {
              setError(null)
              setSelectedSize(value)
              setHasUserSelectedVariant(true)
            }}
          />
        )}

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div style={{ marginTop: '1rem' }}>
                <StyledErrorMessage>{error}</StyledErrorMessage>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {(isSizeAvailable || !isSizeSelected || isGiftCard) && (
          <AddToCart>
            <AddToCartButtonWrapper>
              <Button
                fullWidth
                text={buttonMessage}
                disabled={!isSizeSelected}
                type="submit"
              />

              <WishlistCTA
                layout="button"
                productData={{
                  handle: product.handle ?? '',
                  productId: String(product.productId ?? ''),
                  variantId: selectedVariantId ?? undefined,
                  variantSelectedByUser: hasUserSelectedVariant,
                }}
              />
            </AddToCartButtonWrapper>
            {isGiftCard ? undefined : (
              <SansText>{productTranslation('freeDelivery')}</SansText>
            )}
          </AddToCart>
        )}
      </Form>
      {/* Prevents the sticky bar from appearing late when the user scrolls down,
          because the button is still inside the viewport, but it's hidden by
          the navigation menu and promotional banner.
      */}
      <InView
        rootMargin={`-${navbarHeight + promotionalBannerHeight}px 0px 0px 0px`}
      >
        {({ inView, ref: wrapperRef }) => (
          <div ref={wrapperRef}>
            {stickyBarEl &&
              createPortal(
                <AnimatePresence>
                  {!inView &&
                    !isFooterInView &&
                    (!isSizeSelected || isSizeAvailable || !isDiscontinued) && (
                      <ProductStickyBar
                        product={product}
                        currentSize={currentSize}
                        isSizeSelected={isSizeSelected}
                        isSizeAvailable={isSizeAvailable}
                        buttonMessage={stickyBarButtonMessage}
                        hasOneSize={hasOneSize}
                        error={error}
                        scrollToCallback={() => {
                          setIsBlinking(true)
                        }}
                      />
                    )}
                </AnimatePresence>,
                stickyBarEl,
              )}
          </div>
        )}
      </InView>

      {isSizeSelected && !isSizeAvailable && !isDiscontinued && (
        <>
          <NotifyForm
            completeTitle={globalTranslation('back_in_stock_thank_you_title')}
            completeText={globalTranslation('back_in_stock_thank_you_message')}
            complete={notifyMeComplete}
            placeholder={globalTranslation('back_in_stock_email_address')}
            policyText={globalTranslation('back_in_stock_policy_text')}
            loading={notifyMeLoading}
            email={notifyMeEmail ?? ''}
            title={globalTranslation(getBackInStockTranslationKey(notifyMe))}
            onChange={setNotifyMeEmail}
            onKeyDown={handleKeyDown}
            onSubmit={notifyMeSubmit}
          />
          <NotifySimilarProducts
            title={productTranslation('notifyFormRelatedProductsTitle')}
            productHandle={product.handle || ''}
            selectedSize={currentSize?.name}
          />
        </>
      )}
    </WrapperSizePicker>
  )
}
